import { TelegramAvailableFeaturesFeaturePremiumDto } from '@/generated';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconChevronLeft } from '@tabler/icons-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import clsx from 'clsx';
import { Switch } from '@/components/ui/switch';
import { getFeaturePriceBreakdown } from '@/utils/feature-price';
import { CurrencySign } from '../../common/CurrencySign';
import { Separator } from '@/components/ui/separator';

type PremiumProps = {
  available: TelegramAvailableFeaturesFeaturePremiumDto;
  selectedIds: string[];
  onGoNext: () => void;
  onGoBack: () => void;
  onSelect: (serviceId: string) => void;
  onDeselect: (serviceId: string) => void;
  onClose: () => void;
  hideSwitch?: boolean;
};

export const PremiumComponent = ({
  available,
  selectedIds,
  onGoNext,
  onSelect,
  onClose,
  onGoBack,
  onDeselect,
  hideSwitch = false,
}: PremiumProps) => {
  const { header, text, cardHeader, cardText, discount, id, icon, footerText } = available;
  const price = getFeaturePriceBreakdown(available);
  const t = useTranslations('monetization');
  const selected = selectedIds.includes(id);

  const toggleValue = (value: boolean) => {
    if (value) {
      onSelect(id);
    } else {
      onDeselect(id);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-xl'>
        <Button
          variant='ghost'
          size='icon'
          onClick={onGoBack}
          className='absolute top-4 start-4 z-10 h-8 w-8 rounded-full hover:bg-transparent'
        >
          <IconChevronLeft className='text-text-primary' />
        </Button>
        <DialogHeader className='items-center px-8 text-center sm:text-center'>
          <DialogTitle>{header}</DialogTitle>
          <DialogDescription>{text}</DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-[24px]'>
          <div
            className={clsx(
              'relative py-[14px] px-[12px] flex flex-col bg-surface-light rounded-[12px]',
              'outline outline-[2px] outline-solid transition-[outline-color] duration-200 ease-out',
              hideSwitch || selected ? 'outline-main-green' : 'outline-transparent',
            )}
          >
            {discount ? (
              <div className='absolute start-[14px] top-[-8px] z-1 bg-text-error rounded-[4px] px-[4px] py-[2px] flex justify-center items-center text-[#FFF] text-[12px]'>
                -{discount}%
              </div>
            ) : (
              <></>
            )}

            <div className='flex justify-between'>
              <div className='flex gap-[4px] items-center'>
                <Image src={icon} width={30} height={30} alt='' />
                <div className='text-[17px] leading-[22px] text-black font-medium'>
                  {price.fiatAmount} <CurrencySign value={price.currencySign} />
                </div>
                <div
                  className={clsx('text-[14px] ml-[4px] leading-[20px] text-black opacity-40 line-through', {
                    'opacity-0!': !price.fiatFakeAmount || price.fiatFakeAmount === price.fiatAmount,
                  })}
                >
                  {price.fiatFakeAmount}
                </div>
              </div>
              {!hideSwitch && <Switch onCheckedChange={toggleValue} checked={selected} aria-label={header} />}
            </div>

            <div className='mt-[20px] text-[16px] text-black font-medium leading-[20px]'>{cardHeader}</div>

            {cardText.length > 0 && (
              <div className='mt-[4px] text-[16px] text-primary font-normal leading-[20px]'>{cardText}</div>
            )}
            <Separator className='my-[12px] bg-[#DBDBDB]' />
            <div className='flex justify-between'>
              <div className='text-[14px] text-text-disabled-day leading-[20px]'>{footerText}</div>
              <div className='text-[14px] leading-[20px]'>{t('view_boost_days', { value: 7 })}</div>
            </div>
          </div>

          <div className='flex flex-col gap-[8px]'>
            <p className='text-[16px] font-medium leading-[20px]'>{t('howItWorks')}</p>
            <div className='mt-[12px] flex flex-col gap-[8px]'>
              <div>💎 {t('premium_1')}</div>
              <div>💎 {t('premium_2')}</div>
            </div>
            <div className='flex justify-center w-full rounded-[12px] bg-bg2-day'>
              <Image src='/assets/images/vip-preview.png' alt='' width={283} height={252} />
            </div>
          </div>
        </div>

        <Button onClick={onGoNext} className='w-full h-auto py-[10px]'>
          {t('continue')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
